import { getAuthTokens } from './authService';

// Base API URL for subscription endpoints
const API_BASE_URL = 'https://xoxl3mvo9e.execute-api.us-east-1.amazonaws.com/prod';

/**
 * Format the authorization token
 * @param {string} idToken - The ID token from Cognito
 * @returns {string} - Formatted token without Bearer prefix
 */
const formatToken = (idToken) => {
  if (!idToken) {
    throw new Error('Authentication required. Please log in again.');
  }

  let formattedToken = idToken.trim();

  // Remove Bearer prefix if present
  if (formattedToken.startsWith('Bearer ')) {
    formattedToken = formattedToken.substring(7).trim();
  }

  return formattedToken;
};

/**
 * Create a payment intent for subscription
 * @param {Object} paymentData - Payment intent data
 * @param {string} paymentData.email - Customer's email
 * @param {string} paymentData.organizationName - Organization name
 * @returns {Promise<Object>} - Payment intent details including clientSecret
 */
export const createPaymentIntent = async (paymentData) => {
  try {
    const { email, organizationName } = paymentData;

    // Get the Cognito user token
    const authTokens = getAuthTokens();
    const formattedToken = formatToken(authTokens.idToken);

    console.log('Creating payment intent with:', {
      email,
      organizationName,
      hasToken: !!formattedToken
    });

    const response = await fetch(`${API_BASE_URL}/payment/create-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${formattedToken}`,
      },
      body: JSON.stringify({
        email,
        organizationName,
        isSubscription: true
      }),
    });

    if (!response.ok) {
      let errorText;
      try {
        const errorData = await response.json();
        errorText = errorData.message || JSON.stringify(errorData);
      } catch (e) {
        errorText = await response.text();
      }
      throw new Error(`Failed to create subscription: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    console.log('Payment intent created successfully:', data);

    if (!data.clientSecret) {
      throw new Error('No client secret returned from API');
    }

    return data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Create a subscription with payment method
 * @param {Object} subscriptionData - Subscription data
 * @param {string} subscriptionData.email - Customer's email
 * @param {string} subscriptionData.organizationName - Organization name
 * @param {string} subscriptionData.paymentMethod - Payment method ID from Stripe
 * @returns {Promise<Object>} - Subscription details
 */
export const createSubscription = async (subscriptionData) => {
  try {
    const { email, organizationName, paymentMethod } = subscriptionData;

    // Get the Cognito user token
    const authTokens = getAuthTokens();
    const formattedToken = formatToken(authTokens.idToken);

    const response = await fetch(`${API_BASE_URL}/payment/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${formattedToken}`,
      },
      body: JSON.stringify({
        email,
        organizationName,
        paymentMethod
      }),
    });

    if (!response.ok) {
      let errorText;
      try {
        const errorData = await response.json();
        errorText = errorData.message || JSON.stringify(errorData);
      } catch (e) {
        errorText = await response.text();
      }
      throw new Error(`Failed to create subscription: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    console.log('Subscription created successfully:', data);

    return data;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

export default {
  createPaymentIntent,
  createSubscription
};